<template>
  <v-menu
    bottom
    left
    offset-y
    content-class="rmr-action-menu__content"
  >
    <template v-slot:activator="{ on, attrs }">
      <span v-bind="attrs" v-on="on">
        <rmr-action-button
          v-if="triggerMode === 'button'"
          :label="triggerLabel"
          :icon="triggerIcon"
          :variant="triggerButtonVariant"
          :color="triggerButtonColor"
          :size="size"
          :disabled="triggerDisabled"
        />
        <rmr-icon-button
          v-else
          :icon="triggerIcon"
          :variant="triggerVariant"
          :size="size"
          :disabled="triggerDisabled"
        />
      </span>
    </template>

    <v-list dense class="rmr-action-menu__list">
      <v-list-item
        v-for="action in actions"
        :key="action.key"
        class="rmr-action-menu__item"
        :class="{ 'rmr-action-menu__item--disabled': action.disabled }"
        :disabled="action.disabled"
        @click="$emit('select', action)"
      >
        <v-list-item-icon class="rmr-action-menu__icon">
          <span
            class="rmr-action-menu__icon-badge"
            :style="getActionBadgeStyle(action)"
          >
            <v-icon small :color="action.disabled ? disabledIconColor : action.color">
              {{ action.icon }}
            </v-icon>
          </span>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="rmr-action-menu__title">
            {{ action.label }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { mdiDotsVertical } from "@mdi/js";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import { RMR_SURFACE_2, RMR_TEXT_SUBTLE } from "@/styles/tokens";

export default {
  name: "RmrActionMenu",
  components: { RmrActionButton, RmrIconButton },
  props: {
    actions: {
      type: Array,
      default: () => [],
    },
    triggerMode: {
      type: String,
      default: "icon",
      validator: (value) => ["icon", "button"].includes(value),
    },
    triggerLabel: {
      type: String,
      default: "",
    },
    triggerIcon: {
      type: String,
      default: mdiDotsVertical,
    },
    triggerVariant: {
      type: String,
      default: "neutral",
    },
    triggerButtonVariant: {
      type: String,
      default: "transparent",
    },
    triggerButtonColor: {
      type: String,
      default: null,
    },
    triggerDisabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "sm",
    },
  },
  emits: ["select"],
  data() {
    return {
      disabledIconColor: RMR_TEXT_SUBTLE,
    };
  },
  methods: {
    getActionBadgeStyle(action) {
      if (action.disabled) {
        return {
          backgroundColor: RMR_SURFACE_2,
        };
      }

      const backgroundColor = action.badgeBackground;
      return backgroundColor ? { backgroundColor } : {};
    },
  },
};
</script>

<style lang="scss">
.rmr-action-menu__content {
  z-index: 220 !important;
}

.rmr-action-menu__list {
  padding: 6px !important;
  min-width: 188px;
}

.rmr-action-menu__item {
  border-radius: $rmr-radius-lg !important;
  min-height: 40px !important;
}

.rmr-action-menu__item:hover {
  background: $rmr-surface-2 !important;
}

.rmr-action-menu__item--disabled {
  opacity: 1 !important;
}

.rmr-action-menu__item--disabled:hover {
  background: transparent !important;
}

.rmr-action-menu__icon {
  margin-right: 10px !important;
  min-width: 20px !important;
}

.rmr-action-menu__icon-badge {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
}

.rmr-action-menu__title {
  font-size: $rmr-fs-body !important;
  font-weight: 600 !important;
  color: $rmr-text-primary !important;
}

.rmr-action-menu__item--disabled .rmr-action-menu__title {
  color: $rmr-text-subtle !important;
}

.rmr-action-menu__item--disabled .rmr-action-menu__icon-badge {
  box-shadow: inset 0 0 0 1px $rmr-border-hover;
}
</style>
